import { ExtractComponentProp } from '@approvalmax/types';
import { AddIcon, SearchIcon } from '@approvalmax/ui';
import { Box, BulkActionsPanel, Button, Flex, TextField } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HelpStripe, SubscribeAfterTrialButton } from 'shared/components';
import { SubscribeAfterTrialButtonType } from 'shared/components/SubscribeAfterTrialButton/SubscribeAfterTrialButton.types';
import { getCanShowSubscribeAfterTrialButton } from 'shared/helpers/experiments';

import { showAddUsersPopup, showDeleteUsersPopup, showInviteUsersPopup } from '../../actions';
import { textFieldWidth } from '../UserList/UserList.constants';
import { messages } from './Toolbar.messages';
import { ToolbarProps } from './Toolbar.types';

export const Toolbar: FC<ToolbarProps> = memo((props) => {
    const { triggerThrottledFilterChange, checkedItemIds, setCheckedItemIds, helpStripeId } = props;

    const dispatch = useDispatch();

    const [openAddPopup, _, cleanOpenAddPopup] = hooks.useQueryParam('openAddPopup');
    const company = useSelector(selectors.navigation.getActiveCompany);
    const companyTeam = useSelector((state: State) => selectors.company.getCompanyTeam(state, company));
    const me = useSelector(selectors.profile.getProfileUser);

    const profile = useSelector(selectors.profile.findProfile);
    const canShowSubscribeAfterTrialButton = getCanShowSubscribeAfterTrialButton(company, profile);

    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        setCheckedItemIds([]);
    }, [companyTeam.length, setCheckedItemIds]);

    const getInvitableCheckedUsers = useCallback(
        (): selectors.types.ExpandedCompanyUser[] =>
            checkedItemIds.reduce((users: selectors.types.ExpandedCompanyUser[], itemId) => {
                const foundUser = companyTeam.find((user) => user.id === itemId);

                if (foundUser && foundUser.status !== domain.CompanyUserStatus.Active) {
                    users.push(foundUser);
                }

                return users;
            }, []),
        [checkedItemIds, companyTeam]
    );

    const getDeletableCheckedUsers = useCallback(() => {
        const meId = me.id;

        return checkedItemIds.reduce<selectors.types.ExpandedCompanyUser[]>((acc, itemId) => {
            if (itemId !== meId) {
                const foundCompany = companyTeam.find((item) => item.id === itemId);

                if (foundCompany) {
                    acc.push(foundCompany);
                }
            }

            return acc;
        }, []);
    }, [checkedItemIds, companyTeam, me.id]);

    const inviteBulk = useCallback(() => {
        const users = getInvitableCheckedUsers();

        setCheckedItemIds([]);
        dispatch(showInviteUsersPopup(users));
    }, [dispatch, getInvitableCheckedUsers, setCheckedItemIds]);

    const deleteBulk = useCallback(() => {
        dispatch(showDeleteUsersPopup(getDeletableCheckedUsers().map((x) => x.id)));
        setCheckedItemIds([]);
    }, [dispatch, getDeletableCheckedUsers, setCheckedItemIds]);

    const onFilterChange = useCallback<ExtractComponentProp<typeof TextField, 'onChange'>>(
        (filterText) => {
            setFilterText(filterText);
            triggerThrottledFilterChange(filterText);
        },
        [triggerThrottledFilterChange]
    );

    const addUser = useCallback(() => {
        dispatch(showAddUsersPopup());
    }, [dispatch]);

    useEffect(() => {
        if (!company.isReadonly && openAddPopup) {
            addUser();
            cleanOpenAddPopup();
        }
    }, [addUser, cleanOpenAddPopup, company.isReadonly, openAddPopup]);

    const hasCheckedItems = checkedItemIds.length > 0;

    return (
        <Box spacing='8 0'>
            {!hasCheckedItems ? (
                <Flex spacing='8' alignItems='center'>
                    {company.isReadonly && canShowSubscribeAfterTrialButton && profile ? (
                        <SubscribeAfterTrialButton
                            profileId={profile.id}
                            companyId={company.id}
                            type={SubscribeAfterTrialButtonType.AddUser}
                        />
                    ) : (
                        <Button
                            startIcon={<AddIcon />}
                            size='small'
                            color='blue10'
                            onClick={addUser}
                            disabled={company.isReadonly}
                        >
                            {messages.addUserButtonText}
                        </Button>
                    )}

                    <TextField
                        width={textFieldWidth}
                        size='small'
                        value={filterText}
                        startIcon={<SearchIcon />}
                        onChange={onFilterChange}
                        placeholder={messages.searchPlaceholder}
                    />

                    <HelpStripe.ToggleButton id={helpStripeId} />
                </Flex>
            ) : (
                <BulkActionsPanel
                    selectedItemsCount={checkedItemIds.length}
                    searchValue={filterText}
                    onSearchChange={onFilterChange}
                    onClose={deleteBulk}
                    actions={[
                        <Button
                            key='bulk-invite-users'
                            size='small'
                            color='midnight100'
                            variant='outline'
                            onClick={inviteBulk}
                            disabled={getInvitableCheckedUsers().length === 0 || company.flags.isRetired}
                        >
                            {messages.bulkInvitedUsersText}
                        </Button>,
                    ]}
                />
            )}
        </Box>
    );
});

Toolbar.displayName = 'Toolbar';
